import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  EBadgePosition,
  EBundlesViewModel,
  FontAwesomeIcon
} from '@appcharge/shared-ui';
import useBadges from 'api/useBadges';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import {
  Badge as BadgeType,
  BundleBadge,
  EBadgeType,
  ETaskId,
  Offer,
  OfferBadge,
  OfferUI,
  ProductsSequenceProduct,
  Section
} from 'common/contracts';
import {
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import {
  EAssetType,
  EBundlesInternalViewModel,
  EFeatureFlag,
  EProductPriority,
  EProductType,
  ESaleDiscountDisplayType,
  ESectionOfferType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { transformQuantityValue } from 'utils/formattingUtil';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import {
  getBadgeLabel,
  getBadgePlaceholder,
  shouldRenderBadge
} from 'views/Promotions/utils/badgeHelpers';
import * as yup from 'yup';

import AutoCompleteInput from 'components/AutoCompleteInput/AutoCompleteInput';
import Badge from 'components/Badge/Badge';
import BarPointsForm from 'components/BarPointsForm/BarPointsForm';
import Button from 'components/Button/Button';
import FormHeader from 'components/FormHeader/FormHeader';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import RichText from 'components/RichText/RichText';
import SegmentInput from 'components/SegmentInput/SegmentInput';
import { generatePlayerData } from 'components/TemplatesStudio/Components/FormPage/Preview/utils/playerData';
import Textarea from 'components/Textarea/Textarea';
import { TabsItem } from 'components/Theme/StoreTheme/storeTheme.types';
import Typography from 'components/Typography/Typography';
import { Separator } from 'components/ui/separator';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ProductsTable } from '../../Promotions/components/ProductsTable';

import BundlePreviewSection from './components/BundlePreviewSection';
import { PRODUCT_PRIORITY_OPTIONS, TRANSLATE_MOCK_MAP } from './constants';
import { useBundlePreviewData } from './hooks/useBundlePreviewData';

import 'style/forms.scss';

interface IOffersFormProps {
  edit?: boolean;
  dup?: boolean;
  isPlayerInfoSync?: boolean;
}

const mapViewModelToBadgeType: Record<EBundlesViewModel, EBadgeType> = {
  [EBundlesViewModel.LINEAR]: EBadgeType.EMBLEM,
  [EBundlesViewModel.THREE_IN_ONE]: EBadgeType.RIBBON,
  [EBundlesViewModel.SIX_IN_ONE]: EBadgeType.EMBLEM,
  [EBundlesViewModel.TWO_IN_ONE]: EBadgeType.RIBBON,
  [EBundlesViewModel.TWO_IN_ONE_DYNAMIC]: EBadgeType.RIBBON
};

const LOCALIZED_PRICE_ERROR = 'Price point is not localized';
const DEFAULT_BUNDLE_EXTERNAL_ID = 'Default_Bundle';

export const OffersForm: React.FC<IOffersFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { canUserEdit } = usePermissions();
  const { featureFlags } = useFeatureFlags({ publisherId: publisherId });
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { addOffer, updateOffer, getOffer, getOffers, formatProductQuantity } =
    useOffers({ offerId });
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const [currentOfferUI, setCurrentOfferUI] = useState('');
  const { getOfferUI, getOffersUI } = useOffersUI(publisherId, currentOfferUI);
  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const { getGeneralTheme: generalTheme, getStoreTheme } =
    useTheme(publisherId);
  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { getBadges } = useBadges(publisherId);
  const [badgeList, setBadgeList] = useState<BundleBadge[]>([]);
  const [isNameExists, setIsNameExists] = useState<boolean>(false);
  const { getSectionsByOfferType: getSections } = useSections(
    publisherId,
    ESectionOfferType.BUNDLE
  );
  const { getTabs } = useTabs(publisherId);
  const [badges, setBadges] = useState(getOffer?.data?.result?.badges);
  const [emblemBadges, setEmblemBadges] = useState<object[]>([]);
  const [ribbonBadges, setRibbonBadges] = useState<object[]>([]);
  const [tagsError, setTagsError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dirtyProducts, setDirtyProducts] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [chosenSegment, setChosenSegment] = useState<string[]>([]);
  const [progressBarFormValues, setProgressBarFormValues] = useState<{
    progressBarId: string | null;
    progressBarPoints: number | null;
  }>({
    progressBarId: null,
    progressBarPoints: null
  });
  const [isProgressBarFormValid, setIsProgressBarFormValid] = useState(true);
  const [dirtyProgressBar, setDirtyProgressBar] = useState(false);
  const { getPricings } = usePricing();
  const [isPriceLocalized, setIsPriceLocalized] = useState(true);
  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  if (!getPricings.data && !getPricings.isFetching) {
    getPricings.refetch();
  }
  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;

  const checkUniqueness = (value: any, field: keyof Offer) => {
    const isTaken = getOffers.data?.offers.find((o: any) => {
      if (!edit) return o[field] === value;
      return o[field] === value && data[field] !== value;
    });
    return !isTaken;
  };
  const emptyTab = { content: 'All', key: '0', value: '0' };

  const bundleModel = useMemo(
    () => generalTheme.data?.bundlesInternalViewModel,
    [generalTheme.data?.bundlesInternalViewModel]
  );

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  let offerSchema = yup.object().shape({
    name: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
      .min(3, `Name length should be longer`)
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    description: yup
      .string()
      .nullable()
      .test(
        'is-empty-or-min-length',
        'Description length should be longer',
        (value) => !value || value.length >= 3
      ),
    offerUiId: yup.string().required('Offer Design is required'),
    publisherOfferId: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'publisherOfferId');
      }),
    price: yup
      .number()
      .min(0.8, 'Price must be at least 0.8')
      .max(5000, 'Price must be at most 5000')
      .required('Price is required')
      .test('maxDigits', 'Price must have at most 10 digits', (value) =>
        value ? value.toString().length <= PRICE_VALUE_LIMIT : true
      ),
    discount: yup
      .number()
      .min(0, 'Price discount cannot be negative')
      .max(99, 'Price discount cannot be more than 99%')
      .test(
        'is-one-decimal',
        'Price discount can have one decimal place at most',
        (value) =>
          value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
      ),
    salePercentage: yup
      .number()
      .min(0, 'Sale amount cannot be negative')
      .test(
        'is-one-decimal',
        'Sale amount can have one decimal place at most',
        (value) =>
          value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
      ),
    segments: yup.array().of(yup.string()),
    tags: yup.array().of(yup.string())
  });

  useEffect(() => {
    if (currentOfferUI && publisherId) {
      getOfferUI.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOfferUI, publisherId]);

  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, [edit, data]);

  useEffect(() => {
    data?.segments && setChosenSegment(data?.segments || []);
  }, [data?.segments]);

  const relevantBadgeType: EBadgeType | 'both' = useMemo(() => {
    if (
      bundleModel === EBundlesInternalViewModel.HEART_OF_VEGAS ||
      bundleModel === EBundlesInternalViewModel.LIGHTNING_LINK_CASINO ||
      bundleModel === EBundlesInternalViewModel.DYNAMIC_HEART_OF_VEGAS ||
      bundleModel === EBundlesInternalViewModel.LIGHTNING_LINK_CASINO2 ||
      bundleModel === EBundlesInternalViewModel.PLAYDOG ||
      bundleModel === EBundlesInternalViewModel.STUDIO_DEFAULT_LINEAR ||
      bundleModel === EBundlesInternalViewModel.STUDIO_DEFAULT_TWO_IN_ONE ||
      bundleModel === EBundlesInternalViewModel.STUDIO_DEFAULT_THREE_IN_ONE ||
      bundleModel === EBundlesInternalViewModel.DRIMAGE
    ) {
      return 'both';
    }
    if (
      bundleModel === EBundlesInternalViewModel.GHOST ||
      bundleModel === EBundlesInternalViewModel.JUNES_JOURNEY ||
      bundleModel === EBundlesInternalViewModel.LOAD_COMPLETE ||
      bundleModel === EBundlesInternalViewModel.ROYAL_MATCH
    )
      return EBadgeType.EMBLEM;
    if (
      bundleModel === EBundlesInternalViewModel.PLAY_SIMPLE ||
      bundleModel === EBundlesInternalViewModel.BANDITOS
    )
      return EBadgeType.RIBBON;

    if (viewModel) {
      return mapViewModelToBadgeType[viewModel as EBundlesViewModel];
    }

    return EBadgeType.RIBBON;
  }, [viewModel, bundleModel]);

  const filterRelevantBadges = (badgeType: EBadgeType) => {
    if (
      relevantBadgeType === 'both' ||
      ((viewModel === EBundlesViewModel.LINEAR ||
        viewModel === EBundlesViewModel.TWO_IN_ONE ||
        viewModel === EBundlesViewModel.THREE_IN_ONE ||
        viewModel === EBundlesViewModel.TWO_IN_ONE_DYNAMIC ||
        viewModel === EBundlesViewModel.SIX_IN_ONE) &&
        badgeType === relevantBadgeType)
    ) {
      return true;
    }
    return false;
  };

  const [products, setProducts] = useState<
    {
      _id: string;
      productId: string;
      image: string;
      imagePrefix: string;
      name: string;
      amount?: number | string;
      type: string;
      textFontColorHex: string;
      prefix: string;
      suffix: string;
      publisherProductId?: string;
      quantityDisplay: string;
      priority: string;
    }[]
  >([]);
  const [currentProduct, setCurrentProduct] =
    useState<ProductsSequenceProduct | null>(null);
  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;

  const productsList: Product[] = productsData?.result;

  useEffect(() => {
    if (!!offerId && !getOffer.isLoading && getOffer.data) {
      setData(getOffer.data.result);
      setCurrentOfferUI(
        getOffer.data.result.offerUi?._id ||
          getOffer.data.result.offerUi?.offerUiId ||
          ''
      );
      setProducts(
        getOffer.data.result.productsSequence[0].products.map(
          (
            p: {
              product: Partial<Product>;
              quantity: number;
              quantityDisplay: string;
              priority: EProductPriority;
            },
            index: number
          ) => {
            return {
              _id: p.product?.productId || index,
              productId: p.product?.productId,
              image: p.product?.images?.[0].url,
              imagePrefix: p.product?.images?.find(
                (i) => i.type === EAssetType.PRODUCT_PREFIX
              )?.url,
              name: p.product?.name,
              amount: p?.quantity,
              type: p.product?.type,
              textFontColorHex: p.product?.textFontColorHex,
              prefix: p.product?.prefix,
              suffix: p.product?.suffix,
              publisherProductId: p.product?.publisherProductId,
              quantityDisplay: p.quantityDisplay || '',
              priority: p.priority || EProductPriority.SUB
            };
          }
        )
      );
      setProgressBarFormValues({
        progressBarPoints:
          getOffer.data.result.productsSequence?.[0]?.progressBarPoints?.[0]
            ?.points || null,
        progressBarId:
          getOffer.data.result.productsSequence?.[0]?.progressBarPoints?.[0]
            ?.barId || null
      });
    }
  }, [getOffer.data, getOffer.isLoading, offerId]);

  useEffect(() => {
    if (offerId && publisherId) {
      getOffer.refetch();
    }
  }, [offerId, publisherId]);

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    dirty,
    isValid,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      publisherOfferId: data.publisherOfferId || '',
      price: data.productsSequence?.[0]?.priceInUsdCents
        ? new Decimal(data.productsSequence?.[0]?.priceInUsdCents)
            .div(100)
            .toNumber()
        : MIN_USD_PRICE,
      name: data.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      displayName: data.displayName
        ? `${data.displayName}${dup ? '_copy' : ''}`
        : data.name
          ? `${data.name}${dup ? '_copy' : ''}`
          : '',
      ...(data.description && { description: data.description }),
      type: data.type || '',
      offerUiId:
        edit || dup
          ? edit
            ? data.offerUi?._id || data.offerUi?.offerUiId || ''
            : getOffer?.data?.result?.offerUi?.offerUiId || ''
          : getOffersUI?.data?.result.find(
              (oi: OfferUI) => oi.externalId === DEFAULT_BUNDLE_EXTERNAL_ID
            )?._id || '',
      segments: data.segments || [],
      tags: data.tags || [],
      quantity: '',
      productsSequence: data.productsSequence || [{ products: [] }],
      salePercentage: data?.productSale?.sale || 0,
      salePercentageDisplayType:
        data.productSale?.type || ESaleDiscountDisplayType.PERCENTAGE,
      discount: data?.priceDiscount?.discount || 0,
      priceDiscountDisplayType:
        data.priceDiscount?.type || ESaleDiscountDisplayType.PERCENTAGE,
      active: edit ? data?.active : true,
      badge:
        data?.badges?.find((b) => {
          return filterRelevantBadges(b.badge!.type);
        })?.badge?.badgeId || null,
      publisherBadgeId: data?.badges?.find((b) => {
        return filterRelevantBadges(b.badge!.type);
      })?.badge?.publisherBadgeId,
      section: data?.section?.sectionId || data?.sectionId?.sectionId || '0',
      tab: data?.tab?.tabId || data?.tabId?.tabId || '0',
      backgroundImage: data?.offerUi?.backgroundImage || ''
    },
    onSubmit: async (values) => {
      const offerBadges: OfferBadge[] = [];
      if (values.badge) {
        const publisherBadgeId =
          values?.publisherBadgeId || badges[0].badge.publisherBadgeId;
        const badge: OfferBadge = {
          badgeId: values.badge,
          publisherBadgeId: publisherBadgeId
        };
        offerBadges.push(badge);
      }

      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );

      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        publisherOfferId: values.publisherOfferId,
        name: values.name,
        displayName: values.displayName || values.name,
        ...(values.description && { description: values.description }),
        type: OfferType.BUNDLE,
        active: values?.active,
        offerUiId: values.offerUiId,
        segments: values.segments,
        tags: values.tags,
        badges: offerBadges,
        productSale: {
          sale: values.salePercentage,
          type:
            values.salePercentageDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          discount: values.discount,
          type:
            values.priceDiscountDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              publisherProductId: p.publisherProductId,
              priority: p.priority
            })),
            priceInUsdCents: new Decimal(values.price).mul(100).toNumber(),
            progressBarPoints
          }
        ],
        sectionId:
          values.section && values.section !== '0' ? values.section : null,
        tabId: values.tab && values.tab !== '0' ? values.tab : null
      };

      if (edit && offerId && !dup) {
        updateOffer.mutate(
          { offerId, form: newOffer },
          {
            onSuccess: () => {
              showToast({
                message: 'Offer edited successfully',
                status: EToasterStatus.SUCCESS
              });
              navigate('../');
            },
            onError: () => {
              showToast({
                message: 'Error editing offer',
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        addOffer.mutate(newOffer, {
          onSuccess: () => {
            showToast({
              message: 'New Offer added successfully',
              status: EToasterStatus.SUCCESS
            });
            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.ACTIVATE_FIRST_BUNDLE);
            }
            navigate('../');
          },
          onError: () => {
            showToast({
              message: 'Error adding Offer',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    }
  });

  useEffect(() => {
    if (values.badge) {
      const currentBadge = getBadges?.data?.result?.find(
        (b: any) => b.badgeId === values.badge
      );
      if (currentBadge) {
        setBadges([{ badge: currentBadge }]);
        if (currentBadge?.emblem) {
          setEmblemBadges([currentBadge.emblem]);
        } else {
          setEmblemBadges([]);
        }
        if (currentBadge?.ribbon) {
          setRibbonBadges([currentBadge.ribbon]);
        } else {
          setRibbonBadges([]);
        }
      }
    } else {
      setBadges([]);
      setEmblemBadges([]);
      setRibbonBadges([]);
    }
  }, [values.badge, getBadges?.data?.result]);

  const removeProduct = useCallback(
    (product: { _id: string; productId?: string }) => {
      const productId = product.productId || product._id;
      setDirtyProducts(true);
      setProducts((prevProducts) => {
        return prevProducts.filter((p) => p._id !== product._id);
      });
      setFieldValue(
        'productsSequence[0].products',
        (prevProducts: ProductsSequenceProduct[]) => {
          return prevProducts.filter((p) => p.productId !== productId);
        }
      );
    },
    [setFieldValue]
  );

  const editProduct = useCallback(
    (product: {
      _id: string;
      productId?: string;
      image?: string;
      amount?: number | string;
      publisherProductId?: string;
      priority?: string | number;
    }) => {
      setDirtyProducts(true);
      removeProduct(product);
      setCurrentProduct({
        product: product.image,
        productId: product._id,
        quantity: product.amount || 0,
        publisherProductId: product.publisherProductId,
        priority: String(product.priority || EProductPriority.SUB)
      });
    },
    [removeProduct]
  );

  const handleProductReorder = useCallback(
    (reordered: Array<{ _id: string; productId?: string }>): void => {
      setDirtyProducts(true);
      setProducts(reordered as typeof products);
      const raw = values.productsSequence?.[0]?.products ?? [];
      const currentFormikProducts = (
        Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object' && !Array.isArray(raw)
            ? Object.values(raw)
            : []
      ) as ProductsSequenceProduct[];
      const reorderedFormikProducts = reordered
        .map((p) =>
          currentFormikProducts.find(
            (pr: ProductsSequenceProduct) => pr.productId === p.productId
          )
        )
        .filter((x): x is ProductsSequenceProduct => x != null);
      setFieldValue('productsSequence[0].products', reorderedFormikProducts);
    },
    [setDirtyProducts, setFieldValue, values.productsSequence]
  );

  const addProduct = async () => {
    const foundProduct = productsData!.result.find(
      (product: { productId: string | undefined }) =>
        product.productId === currentProduct?.productId
    );

    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: currentProduct!.quantity?.toString() || '',
      productType: foundProduct.type
    });

    const newProduct = {
      _id: currentProduct!.productId!,
      productId: currentProduct!.productId!,
      image: foundProduct!.images[0].url,
      imagePrefix: foundProduct.images?.find((i: { type: EAssetType }) => {
        return i.type === EAssetType.PRODUCT_PREFIX;
      })?.url,
      name: foundProduct!.name,
      amount: currentProduct!.quantity,
      type: foundProduct!.type,
      textFontColorHex: foundProduct!.textFontColorHex,
      prefix: foundProduct!.prefix,
      suffix: foundProduct!.suffix,
      quantityDisplay: formattedProductQuantity.data,
      priority: currentProduct?.priority || EProductPriority.SUB
    };

    if (currentProduct?.publisherProductId) {
      (newProduct as any).publisherProductId =
        currentProduct.publisherProductId;
    }

    setProducts((prevProducts) => [...prevProducts, newProduct]);

    setFieldValue(
      'productsSequence[0].products',
      (prevProducts: ProductsSequenceProduct[]) => {
        const newProductSequence = {
          productId: newProduct.productId,
          quantity: newProduct.amount,
          priority: newProduct.priority
        };
        if ((newProduct as any)?.publisherProductId) {
          (newProductSequence as any).publisherProductId = (
            newProduct as any
          ).publisherProductId;
        }
        return [...prevProducts, newProductSequence];
      }
    );

    setCurrentProduct({
      productId: '',
      quantity: 0,
      publisherProductId: '',
      priority: EProductPriority.SUB
    });
  };

  useEffect(() => {
    if (!getBadges.data?.result) return;
    const badgesDropdownItems = getBadges.data?.result
      .map((badge: BadgeType) => {
        if (filterRelevantBadges(badge.type))
          return {
            content: badge.name,
            name: badge.name,
            key: badge.badgeId,
            value: badge.badgeId,
            url: badge.emblem?.imageUrl,
            position: badge.position as EBadgePosition
          };
      })
      .filter((b: any) => b !== undefined);
    setBadgeList(badgesDropdownItems);
  }, [getBadges.data, viewModel, bundleModel]);

  const removeTag = (tag: string) => {
    const filteredTags = values.tags.filter((s) => s !== tag);
    setFieldValue('tags', filteredTags);
  };

  useEffect(() => {
    setIsNameExists(
      getOffers.data?.offers.find((o: any) => {
        return o.name === values.name;
      }) && !edit
    );
  }, [values.name]);

  const handleQuantityChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newQuantity =
      e.target.value.length !== 0
        ? transformQuantityValue(e.target.value)
        : e.target.value;
    setCurrentProduct({
      productId: currentProduct?.productId || '',
      quantity: newQuantity,
      publisherProductId: currentProduct?.publisherProductId,
      priority: currentProduct?.priority || EProductPriority.SUB
    });
  };

  const handlePriorityChange = (value: string) => {
    setCurrentProduct({
      productId: currentProduct?.productId || '',
      quantity: currentProduct?.quantity || 0,
      publisherProductId: currentProduct?.publisherProductId,
      priority: value
    });
  };

  const transformPriceValue = (): number => {
    if (!values.price) return MIN_USD_PRICE;
    return String(values.price).length > PRICE_VALUE_LIMIT
      ? parseFloat(String(values.price).slice(0, PRICE_VALUE_LIMIT))
      : values.price;
  };

  const sectionItems = useMemo(() => {
    const items = [{ value: '0', label: 'None' }];
    if (getSections?.data?.result?.length > 0) {
      const parsedSectionsData = getSections.data.result.map(
        (section: Section) => ({
          value: section.sectionId,
          label: section.name
        })
      );
      return items.concat(parsedSectionsData);
    }
    return items;
  }, [getSections?.data?.result]);

  const getTabItems = () => {
    if (!enableTabs) return [];
    let tabsData = [emptyTab];

    if (getTabs.data?.length > 0) {
      const parsedTabsData = getTabs.data.map((tab: TabsItem) => ({
        content: tab.name,
        key: tab.tabId,
        value: tab.tabId
      }));
      return tabsData.concat(parsedTabsData);
    }
    return tabsData;
  };
  const productsListForPreview = useMemo(() => {
    const list =
      products.length > 0
        ? products.map((p) => ({
            quantity: p?.amount,
            quantityDisplay: p?.quantityDisplay,
            images: {
              product: p?.image,
              productPrefix: p?.imagePrefix
            },
            textFontColorHex: p?.textFontColorHex,
            prefix: p?.prefix,
            suffix: p?.suffix,
            priority: p?.priority || EProductPriority.SUB,
            type: 'Quantity' as EProductType
          }))
        : [];
    return list;
  }, [products]);

  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) return [];
    const productIds = new Set(products.map((item) => item.productId));
    const filteredProducts = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filteredProducts.length === 0) return [];
    return filteredProducts.map((product) => ({
      url: product.images?.[0]?.url,
      name: product.name,
      key: product.name,
      value: product.productId
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct =
    getProductsOptions.find(
      (product) => product.value === currentProduct?.productId
    ) || null;

  const priceLocalizedCheckDebounce = (price: number) => {
    if (priceTypingDebounceTimeout.current) {
      clearTimeout(priceTypingDebounceTimeout.current);
    }
    priceTypingDebounceTimeout.current = setTimeout(() => {
      setIsPriceLocalized(isPriceLocalizedUtil(price, getPricings.data.result));
    }, 1000);
  };

  useEffect(() => {
    if (getPricings.data && values.price) {
      priceLocalizedCheckDebounce(
        new Decimal(values.price).times(100).toNumber()
      );
    }
  }, [getPricings.data, values.price]);

  const offerDesignItems = useMemo(
    () =>
      getOffersUI?.data?.result
        .filter((oi: OfferUI) => oi.offerUiType === OfferDesignTypes.Bundle)
        .map((offerUI: OfferUI) => {
          return {
            content: offerUI.name,
            key: offerUI._id,
            value: offerUI._id
          };
        }),
    [getOffersUI?.data?.result]
  );

  const defaultOfferDesignId = useMemo(
    () =>
      getOffersUI?.data?.result.find(
        (oi: OfferUI) => oi.externalId === DEFAULT_BUNDLE_EXTERNAL_ID
      )?._id || '',
    [getOffersUI?.data?.result]
  );

  useEffect(() => {
    if (!offerId) {
      setCurrentOfferUI(defaultOfferDesignId);
    }
  }, [defaultOfferDesignId, offerId]);

  const handleProgressBarFormChange = useCallback(
    (
      values: {
        progressBarId: string | null;
        progressBarPoints: number | null;
      },
      isValid: boolean,
      isDirty: boolean
    ) => {
      setProgressBarFormValues(values);
      setIsProgressBarFormValid(isValid);
      setDirtyProgressBar(isDirty);
    },
    []
  );

  const bootInfo = {
    storeTheme: { ...getStoreTheme.data, storeScreen: {} },
    featureFlags: featureFlags || {}
  };

  const dynamicOfferData = useBundlePreviewData({
    values,
    getOfferUI,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    transformPriceValue
  });

  const previewProps = {
    translate: (key: string) => TRANSLATE_MOCK_MAP[key] || key,
    playerData: generatePlayerData(),
    boot: bootInfo,
    offers: dynamicOfferData
  };

  const formTitle = `${edit ? 'Edit' : 'Create'} Bundle Offer${edit && getOffer.data?.result?.name ? ` (${getOffer.data.result.name})` : ''}`;

  const isFormValid =
    !isLoading &&
    isValid &&
    isProgressBarFormValid &&
    canUserEdit &&
    (!edit || dirty || dirtyProducts || dirtyProgressBar);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        closeFormOnSubmit={false}
        onCloseForm={() => navigate('../')}
        onMainButtonClick={() => submitForm()}
        title={formTitle}
        isFormValid={isFormValid}
        mainButtonText="Save"
        secondaryButtonText={showPreview ? 'Hide Preview' : 'Show Preview'}
        onSecondaryButtonClick={() => setShowPreview(!showPreview)}
      />
      <div className="flex h-[calc(100vh-73px)]">
        <div
          className={`flex justify-center overflow-y-auto px-3 ${showPreview ? 'w-1/2' : 'w-full'}`}
        >
          <div className="flex flex-col h-full pt-10 pb-10 w-[528px] min-h-fit">
            <RichText
              title="General"
              description="Start with setting up your offer mandatory fields"
              className="mb-5"
            />
            <div className="flex flex-col gap-5 mb-2">
              <div className="grid grid-cols-2 gap-5">
                <Input
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isRequired
                  placeholder="Name"
                  state={
                    (touched.name && errors.name) || isNameExists
                      ? 'error'
                      : 'default'
                  }
                  description={
                    (touched.name ? errors.name?.toString() : '') ||
                    (isNameExists ? 'Name already exists' : '')
                  }
                  descriptionDataTestId="bundleNameValidationTxt"
                />
                <Input
                  label="Display Name"
                  name="displayName"
                  value={values.displayName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Display Name"
                  state={
                    touched.displayName && errors.displayName
                      ? 'error'
                      : 'default'
                  }
                  description={
                    touched.displayName ? errors.displayName?.toString() : ''
                  }
                />
              </div>
              <div>
                <Textarea
                  label="Description"
                  name="description"
                  placeholder="Placeholder"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <Input
                  label="External ID"
                  name="publisherOfferId"
                  value={values.publisherOfferId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isRequired
                  placeholder="ID Number"
                  state={
                    Boolean(errors.publisherOfferId) &&
                    (touched.publisherOfferId || dup)
                      ? 'error'
                      : 'default'
                  }
                  description={
                    touched.publisherOfferId || dup
                      ? errors.publisherOfferId?.toString()
                      : ''
                  }
                  descriptionDataTestId="bundleSkuValidationTxt"
                />
                <InputSelectDropdown
                  listDataTestId="dashBundlesOfferDesignList"
                  dataTestId="offerUiId"
                  descriptionDataTestId="bundleOfferDesignValidationTxt"
                  label="Offer Design"
                  isRequired
                  placeholder="Select Offer Design"
                  options={
                    offerDesignItems?.map((item: any) => ({
                      value: item.value,
                      label: item.content
                    })) || []
                  }
                  defaultSelected={
                    offerDesignItems?.find(
                      (item: any) => item.value === values.offerUiId
                    )
                      ? {
                          value: values.offerUiId,
                          label:
                            offerDesignItems.find(
                              (item: any) => item.value === values.offerUiId
                            )?.content || ''
                        }
                      : undefined
                  }
                  onOptionChange={(value) => {
                    setCurrentOfferUI(value);
                    setFieldValue('offerUiId', value);
                  }}
                  description={
                    touched.offerUiId && errors.offerUiId
                      ? errors.offerUiId?.toString()
                      : ''
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <InputSelectDropdown
                  dataTestId="bundleSectionInput"
                  label="Section"
                  placeholder="Select Section"
                  options={sectionItems}
                  defaultSelected={
                    sectionItems.find(
                      (item) => item.value === values.section
                    ) || undefined
                  }
                  onOptionChange={(value) => {
                    setFieldValue('section', value);
                  }}
                />
                <Input
                  descriptionDataTestId="bundlePriceValidationTxt"
                  label="Price"
                  name="price"
                  value={values.price}
                  onChange={(e) => {
                    setTouched({
                      ...touched,
                      price: true
                    });
                    handleChange(e);
                    validateField('price');
                  }}
                  onBlur={handleBlur}
                  isRequired
                  type="number"
                  min={MIN_USD_PRICE}
                  leftIcon={<span>$</span>}
                  leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
                  state={
                    (touched.price && Boolean(errors.price)) ||
                    !isPriceLocalized
                      ? 'error'
                      : 'default'
                  }
                  description={
                    (touched.price ? errors.price?.toString() : '') ||
                    (isPriceLocalized ? '' : LOCALIZED_PRICE_ERROR)
                  }
                />
              </div>
              {enableTabs && (
                <div className="grid grid-cols-2 gap-5">
                  <InputSelectDropdown
                    label="Tab"
                    dataTestId="bundleTabInput"
                    placeholder="Select Tab"
                    options={getTabItems().map((item: any) => ({
                      value: item.value,
                      label: item.content
                    }))}
                    defaultSelected={
                      getTabItems().find(
                        (item: any) => item.value === values.tab
                      )
                        ? {
                            value: values.tab,
                            label:
                              getTabItems().find(
                                (item: any) => item.value === values.tab
                              )?.content || ''
                          }
                        : undefined
                    }
                    onOptionChange={(value) => {
                      setFieldValue('tab', value);
                    }}
                  />
                </div>
              )}
            </div>
            <Separator className="my-9 bg-base-border" />
            <SegmentInput
              value={chosenSegment}
              onChange={(segments) => {
                setChosenSegment(segments);
                setFieldValue('segments', segments);
              }}
              dataTestId="promoSegmentsInpt"
            />
            <Separator className="mt-5 mb-9 bg-base-border" />
            {shouldRenderBadge(relevantBadgeType) && (
              <>
                <RichText
                  className="mb-5"
                  title="Badges"
                  description='Highlight your offer with a badge like "Most Popular" or "Best Value"'
                />
                <div className="grid grid-cols-2 gap-5">
                  <InputSelectDropdown
                    key={`badge-${values.badge || 'none'}`}
                    label={getBadgeLabel(relevantBadgeType)}
                    placeholder={getBadgePlaceholder(relevantBadgeType)}
                    buttonDataTestId="promoBadgesInpt"
                    options={[
                      { value: '', label: 'None' },
                      ...(badgeList?.map((item: any) => ({
                        value: item.value,
                        label: item.content
                      })) || [])
                    ]}
                    defaultSelected={
                      values.badge
                        ? badgeList?.find(
                            (item: any) => item.value === values.badge
                          )
                          ? {
                              value: values.badge,
                              label:
                                badgeList.find(
                                  (item: any) => item.value === values.badge
                                )?.content || ''
                            }
                          : undefined
                        : undefined
                    }
                    onOptionChange={(value) =>
                      setFieldValue('badge', value === '' ? null : value)
                    }
                  />
                  <InputSelectDropdown
                    label="Position"
                    disabled
                    options={[]}
                    defaultSelected={{
                      value: 'Left',
                      label: 'left'
                    }}
                  />
                </div>
              </>
            )}
            <Separator className="my-9 bg-base-border" />
            <RichText
              className="mb-5"
              title="Sale & Discounts"
              description="Enhance your offer by providing greater value at a better price"
            />
            <div className="flex flex-col gap-5 mb-2">
              <div className="grid grid-cols-2 gap-5">
                <Input
                  label="First Product Sale"
                  descriptionDataTestId="bundleProductSaleValidationTxt"
                  name="salePercentage"
                  value={values.salePercentage}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="number"
                  min={0}
                  step={0.1}
                  leftIcon={
                    values?.salePercentageDisplayType ===
                    ESaleDiscountDisplayType.PERCENTAGE ? (
                      <span>%</span>
                    ) : undefined
                  }
                  leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
                  state={
                    touched.salePercentage && errors.salePercentage
                      ? 'error'
                      : 'default'
                  }
                  description={
                    touched.salePercentage
                      ? errors.salePercentage?.toString()
                      : ''
                  }
                />
                <InputSelectDropdown
                  label="Type"
                  dataTestId="productSaleTypeDropDown"
                  placeholder="Select Type"
                  options={[
                    {
                      value: ESaleDiscountDisplayType.PERCENTAGE,
                      label: 'Percentage'
                    },
                    {
                      value: ESaleDiscountDisplayType.MULTIPLIER,
                      label: 'Multiplier'
                    },
                    {
                      value: ESaleDiscountDisplayType.FIXED_AMOUNT,
                      label: 'Fixed amount'
                    }
                  ]}
                  defaultSelected={{
                    value:
                      values?.salePercentageDisplayType ||
                      ESaleDiscountDisplayType.PERCENTAGE,
                    label:
                      values?.salePercentageDisplayType ===
                      ESaleDiscountDisplayType.PERCENTAGE
                        ? 'Percentage'
                        : values?.salePercentageDisplayType ===
                            ESaleDiscountDisplayType.MULTIPLIER
                          ? 'Multiplier'
                          : 'Fixed amount'
                  }}
                  onOptionChange={(value) =>
                    setFieldValue('salePercentageDisplayType', value)
                  }
                  buttonDataTestId="productSaleTypeDropDown"
                />
              </div>
              {values.price > 0 && (
                <div className="grid grid-cols-2 gap-5">
                  <Input
                    label="Price Discount"
                    dataTestId="bundlePriceDiscountInpt"
                    descriptionDataTestId="bundlePriceDiscountValidationTxt"
                    name="discount"
                    value={values.discount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="number"
                    min={0}
                    step={0.1}
                    leftIcon={
                      values?.priceDiscountDisplayType ===
                      ESaleDiscountDisplayType.PERCENTAGE ? (
                        <span>%</span>
                      ) : undefined
                    }
                    leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
                    state={
                      touched.discount && errors.discount ? 'error' : 'default'
                    }
                    description={
                      touched.discount ? errors.discount?.toString() : ''
                    }
                  />
                  <InputSelectDropdown
                    label="Type"
                    dataTestId="priceDiscountTypeDropDown"
                    placeholder="Select Type"
                    disabled
                    options={[
                      {
                        value: ESaleDiscountDisplayType.PERCENTAGE,
                        label: 'Percentage'
                      },
                      {
                        value: ESaleDiscountDisplayType.MULTIPLIER,
                        label: 'Multiplier'
                      },
                      {
                        value: ESaleDiscountDisplayType.FIXED_AMOUNT,
                        label: 'Fixed amount'
                      }
                    ]}
                    defaultSelected={{
                      value:
                        values?.priceDiscountDisplayType ||
                        ESaleDiscountDisplayType.PERCENTAGE,
                      label:
                        values?.priceDiscountDisplayType ===
                        ESaleDiscountDisplayType.PERCENTAGE
                          ? 'Percentage'
                          : values?.priceDiscountDisplayType ===
                              ESaleDiscountDisplayType.MULTIPLIER
                            ? 'Multiplier'
                            : 'Fixed amount'
                    }}
                    onOptionChange={(value) =>
                      setFieldValue('priceDiscountDisplayType', value)
                    }
                    buttonDataTestId="priceDiscountTypeDropDown"
                  />
                </div>
              )}
            </div>
            <Separator className="my-9 bg-base-border" />
            <RichText
              className="mb-5"
              title="Progress Bar"
              description="Track and update progress for your products"
            />
            <BarPointsForm
              initialValues={{
                progressBarId: progressBarFormValues.progressBarId,
                progressBarPoints: progressBarFormValues.progressBarPoints
              }}
              onChange={handleProgressBarFormChange}
            />
            <Separator className="my-9 bg-base-border" />
            <RichText
              className="mb-5"
              title="Products"
              description="Add products to your offer"
            />
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-3 gap-5">
                <AutoCompleteInput
                  label="Product name"
                  options={getProductsOptions}
                  placeholder="Search product"
                  onChange={(newValue: any) => {
                    setCurrentProduct({
                      ...currentProduct,
                      productId: newValue?.value || '',
                      quantity: currentProduct?.quantity || 0,
                      priority:
                        currentProduct?.priority || EProductPriority.SUB,
                      publisherProductId:
                        currentProduct?.publisherProductId || ''
                    });
                  }}
                  currentOption={selectedProduct}
                  dataTestId="dashBundleProductList"
                  className="truncate"
                />
                <InputSelectDropdown
                  label="Products Type"
                  placeholder="Select Priority"
                  options={PRODUCT_PRIORITY_OPTIONS}
                  listDataTestId="bundleMainSubInpt"
                  defaultSelected={{
                    value: currentProduct?.priority || EProductPriority.SUB,
                    label: currentProduct?.priority || EProductPriority.SUB
                  }}
                  onOptionChange={handlePriorityChange}
                />
                <Input
                  label="Quantity"
                  name="quantity"
                  value={currentProduct?.quantity || ''}
                  type="number"
                  min={0}
                  onChange={handleQuantityChange}
                />
              </div>
              <Button
                variant="secondary"
                disabled={
                  !currentProduct?.productId || !currentProduct?.quantity
                }
                onClick={addProduct}
                leftIcon={
                  <FontAwesomeIcon
                    icon="fa-regular fa-circle-plus"
                    className="w-5 h-5"
                  />
                }
                className="w-full"
                dataTestId="bundleAddProductBtn"
              >
                Add Product
              </Button>
              <ProductsTable
                products={products}
                onEdit={editProduct}
                onDelete={removeProduct}
                onReorder={handleProductReorder}
              />
            </div>
            {hasFeatureFlagTags && (
              <>
                <Separator className="my-5 bg-base-border" />
                <RichText
                  title="Tags"
                  description="Create or add tags to your offer"
                  className="mt-4 mb-5"
                />
                <div className="flex flex-col gap-5 mb-9">
                  <div>
                    <Typography className="text-black font-medium text-sm mb-2">
                      Tag Name
                    </Typography>
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      placeholder="Search Tag"
                      rightIcon={
                        <FontAwesomeIcon
                          icon="fa-regular fa-magnifying-glass"
                          className="w-4 h-4"
                        />
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inputValue.trim()) {
                          e.preventDefault();
                          setFieldValue('tags', [
                            ...values.tags,
                            inputValue.trim()
                          ]);
                          setInputValue('');
                          inputRef?.current?.blur();
                        }
                      }}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        setTagsError(
                          values.tags.includes(e.target.value)
                            ? "You can't add the same tag twice"
                            : ''
                        );
                      }}
                      onBlur={handleBlur}
                      state={tagsError ? 'error' : 'default'}
                      description={tagsError || ''}
                    />
                    <Typography className="text-zinc-500 text-sm mt-2">
                      Press 'Enter' to add a new tag
                    </Typography>
                    {!!values.tags.length && (
                      <div className="mt-3 flex flex-row flex-wrap gap-2">
                        {values.tags.map((val: string) => (
                          <Badge
                            key={val}
                            isRemovable
                            onRemove={() => removeTag(val)}
                          >
                            {val}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {showPreview && (
          <BundlePreviewSection
            previewProps={previewProps}
            storeThemeData={getStoreTheme.data}
          />
        )}
      </div>
    </motion.div>
  );
};
